import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tv, Wifi, Cloud, Shield, ChevronRight, Play, Zap, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [broadcasters, setBroadcasters] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [broadcastersRes, statsRes] = await Promise.all([
          axios.get(`${API}/broadcasters`),
          axios.get(`${API}/stats`)
        ]);
        setBroadcasters(broadcastersRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const dynamicStats = stats ? [
    { value: `${stats.total_channels}+`, label: 'Channels' },
    { value: `${stats.total_broadcasters}+`, label: 'Broadcasters' },
    { value: '4K', label: 'Resolution' },
    { value: '24/7', label: 'Support' }
  ] : [
    { value: '800+', label: 'Channels' },
    { value: '14+', label: 'Broadcasters' },
    { value: '4K', label: 'Resolution' },
    { value: '24/7', label: 'Support' }
  ];

  const features = [
    {
      icon: Wifi,
      title: 'Fiber Optic Technology',
      description: 'Pure glass fiber ensures uninterrupted signal quality throughout the year'
    },
    {
      icon: Tv,
      title: '4K Crystal Clear',
      description: 'Watch your favorite shows in stunning 4K resolution with zero pixelation'
    },
    {
      icon: Cloud,
      title: 'Monsoon Proof',
      description: 'No signal drops even during heavy rains - unlike DTH or cable'
    },
    {
      icon: Shield,
      title: 'TRAI Compliant',
      description: 'All channel packs follow TRAI regulations with transparent pricing'
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section bg-white relative" data-testid="hero-section">
        <div className="hero-bg-pattern" />
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[70vh] py-12">
            {/* Hero Content */}
            <div className="animate-fadeIn">
              <div className="badge badge-featured mb-4">
                <Zap size={12} className="mr-1" />
                Fiber Powered IPTV
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-gray-900 mb-6">
                Crystal Clear
                <span className="text-[#D92323] block">4K Entertainment</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-lg">
                Experience uninterrupted TV entertainment through pure glass fiber technology. 
                No more signal drops during monsoons.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/channels" className="btn-primary inline-flex items-center gap-2" data-testid="hero-view-channels">
                  <Play size={18} strokeWidth={2} />
                  View Channels
                </Link>
                <Link to="/contact" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-contact-us">
                  Contact Us
                  <ChevronRight size={18} strokeWidth={2} />
                </Link>
              </div>
            </div>

            {/* Hero Video/Image Section */}
            <div className="relative animate-fadeIn stagger-2">
              {/* Promo Visual with Logo */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-6">
                <img 
                  src="/hero-promo.png" 
                  alt="A Digital TV - Crystal Clear 4K Entertainment" 
                  className="w-full aspect-video object-cover"
                />
                {/* A Digital Logo Overlay */}
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg">
                  <img src="/Logo-for-web.png" alt="A Digital" className="h-10 w-auto" />
                </div>
                {/* Gradient overlay for visual depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-[#D92323]/90 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform cursor-pointer">
                    <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  </div>
                </div>
              </div>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-3">
                {dynamicStats.map((stat, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl p-4 shadow-lg shadow-gray-100/50 text-center"
                  >
                    <div className="text-2xl md:text-3xl font-bold text-[#D92323] font-mono-condensed mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section bg-[#F9FAFB]" data-testid="features-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="badge badge-default mb-4">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The A Digital Advantage
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Unlike traditional DTH or local cable, we deliver entertainment through pure glass fiber
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon size={24} className="text-[#D92323]" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Broadcasters Preview Section */}
      <section className="section bg-white" data-testid="broadcasters-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
              <span className="badge badge-default mb-4">Our Partners</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Premium Broadcasters
              </h2>
              <p className="text-gray-600 mt-2">
                Partnership with major networks ensures the best channel packs
              </p>
            </div>
            <Link 
              to="/channels" 
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="view-all-channels"
            >
              View All Channels
              <ChevronRight size={18} strokeWidth={2} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {broadcasters.map((broadcaster) => (
                <Link
                  key={broadcaster.id}
                  to={`/channels/${broadcaster.id}`}
                  className="broadcaster-card bg-white border border-gray-100 rounded-2xl p-6 hover:border-[#D92323]/20 group"
                  data-testid={`broadcaster-card-${broadcaster.id}`}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-white font-bold text-lg"
                    style={{ backgroundColor: broadcaster.logo_color }}
                  >
                    {broadcaster.name.charAt(0)}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#D92323] transition-colors">
                    {broadcaster.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {broadcaster.channels.length} Channels
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Highlight Section */}
      <section className="section bg-[#0A0A0A] text-white" data-testid="pricing-highlight">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="badge bg-red-500/20 text-red-400 mb-4">Transparent Pricing</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Same Price,<br />All India Coverage
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                Whether you're in Bangalore, Delhi, or any other state - our channel packages 
                are priced the same everywhere. No hidden charges, no regional variations.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'TRAI compliant transparent pricing',
                  'Choose channels à la carte or in bouquets',
                  'Monthly billing with no lock-in',
                  'All states and union territories covered'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-green-500 flex-shrink-0" strokeWidth={2} />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/channels" className="btn-primary inline-flex items-center gap-2" data-testid="explore-packages">
                Explore Packages
                <ChevronRight size={18} strokeWidth={2} />
              </Link>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8">
              <div className="text-center mb-6">
                <span className="text-sm text-gray-400 uppercase tracking-wider">Starting From</span>
                <div className="text-5xl font-bold text-white font-mono-condensed mt-2">
                  ₹5<span className="text-lg text-gray-400">/channel</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Basic Bouquets</span>
                    <span className="text-white font-bold">From ₹13</span>
                  </div>
                </div>
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Regional Packs</span>
                    <span className="text-white font-bold">From ₹18</span>
                  </div>
                </div>
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">HD Premium Packs</span>
                    <span className="text-white font-bold">From ₹35</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-to-br from-red-600 to-red-700" data-testid="cta-section">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Experience Crystal Clear TV?
          </h2>
          <p className="text-red-100 text-lg mb-8 max-w-2xl mx-auto">
            Get started with A Digital today. Our team will help you choose the perfect package for your family.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:9019206699" className="bg-white text-red-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors" data-testid="cta-call">
              Call 9019206699
            </a>
            <Link to="/contact" className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition-colors" data-testid="cta-contact">
              Send Message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
